/**
 * Lullette PDP enhancements (Horizon 4.3). Vanilla JS, no dependencies beyond the theme import map.
 *
 * Loaded as a module by `snippets/lullette-pdp-assets.liquid`, so it runs once per page however
 * many blocks render it. Everything here is progressive: every block server-renders a correct,
 * readable state, and this file only keeps it in sync or adds dates.
 *
 * Hooks used (all existing Horizon / Shopify contracts, nothing replaced):
 *  - StandardEvents.productSelect ('shopify:product:select') from variant-picker.js. Its
 *    `event.promise` resolves with `detail.html`, the freshly rendered product page for the new
 *    variant. We copy our own server-rendered block markup out of that document.
 *  - ThemeEvents.quantitySelectorUpdate from component-quantity-selector.js, to keep the price
 *    inside the add-to-cart button equal to unit price x quantity.
 *  - Horizon's product-form.js already morphs [ref="addToCartButton"] from `detail.html` on every
 *    variant change, so the server-rendered "· $140" in the button updates without help here.
 */
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/** Events from product cards and quick-add dialogs are not about the page's product. */
const isForeignSource = (target) =>
  target instanceof Element && Boolean(target.closest('product-card, dialog, quick-add-component'));

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

/* -------------------------------------------------------------------------------------------- */
/* Variant sync: <lullette-variant-sync data-key="<block id>" data-product-id="<id>">            */
/* -------------------------------------------------------------------------------------------- */

class LulletteVariantSync extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onProductSelect = async (event) => {
    if (isForeignSource(event.target) || !event.promise) return;

    let result;
    try {
      result = await event.promise;
    } catch {
      return; // aborted by a newer selection, or the fetch failed; keep the current markup
    }

    const detail = result?.detail;
    if (!detail?.html) return;

    if (detail.newProduct?.id) {
      this.dataset.productId = String(detail.newProduct.id);
    } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
      return;
    }

    const key = this.dataset.key;
    if (!key) return;
    const fresh = detail.html.querySelector(`lullette-variant-sync[data-key="${CSS.escape(key)}"]`);
    if (!fresh) return;

    morph(this, fresh);
  };
}

if (!customElements.get('lullette-variant-sync')) {
  customElements.define('lullette-variant-sync', LulletteVariantSync);
}

/* -------------------------------------------------------------------------------------------- */
/* Arrival estimate: <lullette-arrival data-min="9" data-max="15" data-text="..." data-skip="">  */
/* -------------------------------------------------------------------------------------------- */

const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Adds `count` business days to `start` (Mon-Fri, skipping any ISO dates in `skip`).
 * Day 0 is today; the first counted day is the next business day.
 * @param {Date} start
 * @param {number} count
 * @param {Set<string>} skip
 */
export function addBusinessDays(start, count, skip = new Set()) {
  const date = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 12);
  let added = 0;
  while (added < count) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day === 0 || day === 6) continue;
    if (skip.has(isoDay(date))) continue;
    added += 1;
  }
  return date;
}

class LulletteArrival extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    const min = Number.parseInt(this.dataset.min ?? '', 10);
    const max = Number.parseInt(this.dataset.max ?? '', 10);
    const template = this.dataset.text;
    const target = this.querySelector('[data-arrival-line]');
    if (!target || !template || !Number.isFinite(min) || !Number.isFinite(max) || max < min) return;

    const skip = new Set((this.dataset.skip ?? '').match(/\d{4}-\d{2}-\d{2}/g) ?? []);
    const today = new Date();
    const first = addBusinessDays(today, min, skip);
    const last = addBusinessDays(today, max, skip);

    // Show the year only when the range spans New Year ("Dec 28, 2026 – Jan 8, 2027").
    const spansYears = first.getFullYear() !== last.getFullYear();
    const format = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      ...(spansYears ? { year: 'numeric' } : {}),
    });
    // Non-breaking spaces keep "Nov 12 – Nov 20" on one line.
    const nb = (s) => s.replace(/\s/g, ' ');
    const start = nb(format.format(first));
    const end = nb(format.format(last));

    const text = template
      .replace(/\[start\]\s*[–—-]\s*\[end\]/, `${start} – ${end}`)
      .replace('[start]', start)
      .replace('[end]', end);

    target.textContent = text;
    target.setAttribute('data-dated', 'true');
    this.dataset.first = isoDay(first);
    this.dataset.last = isoDay(last);
  }
}

if (!customElements.get('lullette-arrival')) {
  customElements.define('lullette-arrival', LulletteArrival);
}

/* -------------------------------------------------------------------------------------------- */
/* Send a hint: <lullette-hint> wrapping an <a href="mailto:..."> fallback                       */
/* -------------------------------------------------------------------------------------------- */

class LulletteHint extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-hint-link]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (typeof navigator.share !== 'function') return; // let the mailto: link do its job

    const data = {
      title: link.dataset.shareTitle || document.title,
      text: link.dataset.shareText || '',
      url: link.dataset.shareUrl || window.location.href,
    };
    if (typeof navigator.canShare === 'function' && !navigator.canShare(data)) return;

    event.preventDefault();
    try {
      await navigator.share(data);
      this.#announce(link.dataset.sharedText || '');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return; // shopper closed the sheet
      window.location.href = link.href; // share failed for another reason: fall back to email
    }
  };

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-hint-status]');
    if (!status || !message) return;
    status.textContent = message;
    window.setTimeout(() => {
      status.textContent = '';
    }, 4000);
  }
}

if (!customElements.get('lullette-hint')) {
  customElements.define('lullette-hint', LulletteHint);
}

/* -------------------------------------------------------------------------------------------- */
/* Price inside the add-to-cart button: unit price x quantity                                    */
/* Markup comes from the edited blocks/add-to-cart.liquid:                                       */
/*   <span class="lul-atc-price" data-unit-price="14000" data-currency="USD"> · $140</span>      */
/* -------------------------------------------------------------------------------------------- */

/**
 * @param {number} cents
 * @param {string} currency
 */
function formatPrice(cents, currency) {
  const whole = cents % 100 === 0;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(whole ? 0 : 2)}`;
  }
}

function refreshButtonPrices() {
  for (const span of document.querySelectorAll('.lul-atc-price[data-unit-price]')) {
    if (!(span instanceof HTMLElement)) continue;
    const form = span.closest('product-form-component');
    const input = form?.querySelector('input[name="quantity"]');
    const quantity = input instanceof HTMLInputElement ? Number.parseInt(input.value, 10) : 1;
    const unit = Number.parseInt(span.dataset.unitPrice ?? '', 10);
    if (!Number.isFinite(unit)) continue;
    const qty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    const label = span.querySelector('[data-atc-amount]');
    if (label) label.textContent = formatPrice(unit * qty, span.dataset.currency ?? 'USD');
  }
}

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  // Cart drawer quantity changes carry a cartLine; only PDP selectors matter here.
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (detail?.cartLine) return;
  refreshButtonPrices();
});

document.addEventListener('input', (event) => {
  const target = event.target;
  if (target instanceof HTMLInputElement && target.name === 'quantity' && target.closest('product-form-component')) {
    refreshButtonPrices();
  }
});

document.addEventListener(StandardEvents.productSelect, async (event) => {
  const promise = /** @type {Event & { promise?: Promise<any> }} */ (event).promise;
  if (isForeignSource(event.target) || !promise) return;
  try {
    await promise;
  } catch {
    return;
  }
  // product-form.js morphs the button in its own continuation of the same promise; wait a frame
  // so we read the morphed button (unit price for the new variant) and reapply the quantity.
  await nextFrame();
  refreshButtonPrices();
});

refreshButtonPrices();
